import { PerformanceMetric } from "./performanceMetricsService";
import httpService from "./httpService";
import {
  _resetFactorsRequestGuardForTesting,
  runWithFactorsRequestGuard,
} from "./factorsRequestLimiter";

export type ProductFactorType = "basket" | "portfolio";

export interface FactorEntry {
  t: string;
  f: number;
}

export interface FactorsResponse {
  currentPage: number;
  currentPageNumOfItems: number;
  totalPages: number;
  totalItems: number;
  items: FactorEntry[];
}

export interface ProductFactorsBatchRequest {
  productType: ProductFactorType;
  productId: number;
  from?: string;
  to?: string;
}

export interface ProductFactorsBatchResult extends ProductFactorsBatchRequest {
  factors: FactorEntry[];
}

const PAGE_SIZE = 10000;
const PAGE_FETCH_CONCURRENCY = 5;
const BATCH_FETCH_CONCURRENCY = 5;

const FACTORS_CACHE_TTL_MS = 30 * 60 * 1000;
const FACTORS_CACHE_SWEEP_INTERVAL_MS = 10 * 60 * 1000;

interface FactorsCacheEntry {
  factors: FactorEntry[];
  fetchedAt: number;
}

async function mapWithConcurrency<T, R>(
  items: T[],
  concurrency: number,
  mapper: (item: T, index: number) => Promise<R>
): Promise<R[]> {
  if (items.length === 0) return [];

  const safeConcurrency = Math.max(1, Math.min(concurrency, items.length));
  const results = new Array<R>(items.length);
  let nextIndex = 0;

  const worker = async () => {
    while (true) {
      const currentIndex = nextIndex;
      nextIndex += 1;

      if (currentIndex >= items.length) return;
      results[currentIndex] = await mapper(items[currentIndex], currentIndex);
    }
  };

  await Promise.all(Array.from({ length: safeConcurrency }, () => worker()));
  return results;
}

export type FactorsChartEntityType =
  | "PORTFOLIO"
  | "BASKET"
  | "CONTRACT"
  | "STRATEGY"
  | "MANAGED_STRATEGY";

export interface FactorsChartMetric extends Omit<PerformanceMetric, "entityType"> {
  entityType: FactorsChartEntityType;
}

export function factorsToPerformanceMetrics(
  factors: FactorEntry[],
  entityId: number,
  entityType: FactorsChartEntityType
): FactorsChartMetric[] {
  return factors.map((factor) => ({
    id: 0,
    entityId,
    entityType,
    metricName: "factor",
    metricValue: factor.f,
    metricDate: factor.t,
    createdAt: factor.t,
    createdBy: "system",
    updatedAt: factor.t,
    updatedBy: null,
    creator: {
      id: "system",
      email: "system@plutus.local",
    },
    updater: null,
  }));
}

class ProductFactorsService {
  private factorsCache = new Map<string, FactorsCacheEntry>();
  private pendingRequests = new Map<string, Promise<FactorEntry[]>>();
  private lastSweepAt = 0;

  async getFactorsPage(
    productType: ProductFactorType,
    productId: number,
    page: number,
    from?: string,
    to?: string,
    pageSize: number = PAGE_SIZE
  ): Promise<FactorsResponse> {
    const params = new URLSearchParams();
    params.set("page", String(page));
    params.set("pageSize", String(pageSize));
    if (from) params.set("from", from);
    if (to) params.set("to", to);

    const response = await runWithFactorsRequestGuard(() =>
      httpService.get<FactorsResponse>(
        `/v3/products/factors/${productType}/${productId}/provider?${params.toString()}`
      )
    );
    return response.data;
  }

  async getFactors(
    productType: ProductFactorType,
    productId: number,
    from?: string,
    to?: string
  ): Promise<FactorEntry[]> {
    if (from || to) {
      return this.fetchAllPages(productType, productId, from, to);
    }

    this.sweepExpiredEntries();

    const cacheKey = `${productType}-${productId}`;
    const now = Date.now();

    const cached = this.factorsCache.get(cacheKey);
    if (cached && now - cached.fetchedAt < FACTORS_CACHE_TTL_MS) {
      return cached.factors;
    }

    const pending = this.pendingRequests.get(cacheKey);
    if (pending) return pending;

    const promise = this.fetchAllPages(productType, productId)
      .then((factors) => {
        if (this.pendingRequests.get(cacheKey) === promise) {
          this.factorsCache.set(cacheKey, { factors, fetchedAt: Date.now() });
        }
        return factors;
      })
      .finally(() => {
        if (this.pendingRequests.get(cacheKey) === promise) {
          this.pendingRequests.delete(cacheKey);
        }
      });

    this.pendingRequests.set(cacheKey, promise);
    return promise;
  }

  private async fetchAllPages(
    productType: ProductFactorType,
    productId: number,
    from?: string,
    to?: string
  ): Promise<FactorEntry[]> {
    const firstPage = await this.getFactorsPage(productType, productId, 1, from, to);
    const pages: FactorsResponse[] = [firstPage];

    if (firstPage.totalPages > 1) {
      const remainingPageNumbers = Array.from(
        { length: firstPage.totalPages - 1 },
        (_, index) => index + 2
      );
      const remainingPages = await mapWithConcurrency(
        remainingPageNumbers,
        PAGE_FETCH_CONCURRENCY,
        (page) => this.getFactorsPage(productType, productId, page, from, to)
      );
      pages.push(...remainingPages);
    }

    return pages
      .flatMap((page) => page.items)
      .sort((a, b) => new Date(a.t).getTime() - new Date(b.t).getTime());
  }

  private sweepExpiredEntries(): void {
    const now = Date.now();
    if (now - this.lastSweepAt < FACTORS_CACHE_SWEEP_INTERVAL_MS) return;
    this.lastSweepAt = now;

    for (const [key, entry] of this.factorsCache) {
      if (now - entry.fetchedAt >= FACTORS_CACHE_TTL_MS) {
        this.factorsCache.delete(key);
      }
    }
  }

  _resetForTesting(): void {
    this.factorsCache.clear();
    this.pendingRequests.clear();
    this.lastSweepAt = 0;
    _resetFactorsRequestGuardForTesting();
  }

  async getFactorsBatch(
    products: ProductFactorsBatchRequest[],
    concurrency: number = BATCH_FETCH_CONCURRENCY
  ): Promise<ProductFactorsBatchResult[]> {
    return mapWithConcurrency(products, concurrency, async (product) => {
      try {
        const factors = await this.getFactors(
          product.productType,
          product.productId,
          product.from,
          product.to
        );
        return { ...product, factors };
      } catch (error) {
        console.error(
          `Failed to load factors for ${product.productType}:${product.productId} in batch request:`,
          error
        );
        return { ...product, factors: [] };
      }
    });
  }
}

export const productFactorsService = new ProductFactorsService();
